import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import {
  Row,
  Form,
  Spin,
  Alert,
  Input,
  Result,
  Button,
} from 'antd';

import {
  LoadingOutlined,
} from '@ant-design/icons';

import CustomButton from '../customButton/CustomButton';
import learnerRequests from '../../helpers/requests/learnerRequests';

/**
 * @param {String} learnerAction - One of [force-activation, force-password]
 */
const LearnerManagement = ({ learnerAction }) => {
  const [form] = Form.useForm();
  const { siteId, accountId } = useParams();

  const controller = new AbortController();

  const requestInfoInitialState = ({
    status: 'waiting',
    error: false,
    message: {
      title: '',
      description: '',
    },
  });

  const [requestInfo, setRequestInfo] = useState(requestInfoInitialState);

  const TEXT_BODY_ACTIVATION = 'Sometimes you may need to force the activation of a user, for example in case they didn \'t receive the activation email.';
  const TEXT_BODY_FORCE_PASSWORD = 'This will override the user password and the user will get a notification in their email about the operation.';

  const resetStates = () => {
    setRequestInfo(requestInfoInitialState);
    form.resetFields();
  };

  const tryAgain = () => setRequestInfo({
    ...requestInfo,
    status: 'waiting',
  });

  const request = async (formData) => {
    try {
      setRequestInfo({
        ...requestInfo,
        status: 'loading',
      });

      let sendRequest;

      if (learnerAction === 'force-activation') {
        sendRequest = await learnerRequests.forceActivation(
          siteId,
          { ...formData },
          controller,
        );
      } else {
        sendRequest = await learnerRequests.forcePassword(siteId, { ...formData }, controller);
      }

      if (sendRequest?.status === 200) {
        setRequestInfo({
          status: 'success',
          error: true,
          message: {
            title: '',
            description: '',
          },
        });
      }
    } catch (error) {
      if (error?.response?.status === 403) {
        return setRequestInfo({
          status: 'error',
          error: true,
          message: {
            title: 'Forbidden',
            description: 'You do not have permission to perform this action, contact to support.',
          },
        });
      }

      return setRequestInfo({
        status: 'error',
        error: true,
        message: {
          title: 'Whops!',
          description: 'Something went wrong! Please try again later.',
        },
      });
    }

    return null;
  };

  useEffect(() => () => controller.abort(), []);
  useEffect(() => () => resetStates(), []);

  const Loading = () => (
    <Result
      icon={<Spin indicator={<LoadingOutlined style={{ fontSize: 30 }} spin />} />}
      title="Creating ticket..."
    />
  );

  const Error = () => (
    <Result
      status="error"
      title={`Submission Failed (${requestInfo.message.title})`}
    >
      <Alert
        type="error"
        message={requestInfo.message.description}
      />
      <Row align="center" justify="center">
        <Button
          key="try-again"
          onClick={tryAgain}
          style={{ marginTop: 10 }}
        >
          Try Again
        </Button>
      </Row>
    </Result>
  );

  const Success = () => (
    <Result
      status="success"
      title="Your ticket has been sent!"
      subTitle={
        (
          <p>
            You can check its progress
            {' '}
            <Link
              key="go-to-tickets"
              to={`/accounts/${accountId}/support/`}
              target="_blank"
              style={{ textDecoration: 'none' }}
            >
              Here
            </Link>
          </p>
        )
      }
      extra={[
        <CustomButton type="button" onClick={resetStates} key="open-new-ticket">
          Open new ticket
        </CustomButton>,
      ]}
    />
  );

  const FormLearner = () => (
    <Form
      form={form}
      name="basic"
      scrollToFirstError
      onFinish={request}
      layout="vertical"
      size="large"
    >
      <Form.Item
        label="Email"
        name="email"
        rules={[{ type: 'email', required: true, message: 'This field is required' }]}
      >
        <Input placeholder="example@email.com" />
      </Form.Item>

      {
        learnerAction === 'force-password' && (
          <Form.Item
            label="New password"
            name="password"
            rules={[{ required: true, message: 'This field is required' }]}
          >
            <Input.Password />
          </Form.Item>
        )
      }

      <CustomButton type="submit" variant="buttonForm">
        {
          learnerAction === 'force-activation'
            ? 'Activate'
            : 'Change password'
        }
      </CustomButton>
    </Form>
  );

  return (
    <div>
      <p>
        {learnerAction === 'force-activation'
          ? TEXT_BODY_ACTIVATION
          : TEXT_BODY_FORCE_PASSWORD}
      </p>
      <br />
      {requestInfo.status === 'waiting' && <FormLearner />}
      {requestInfo.status === 'loading' && <Loading />}
      {requestInfo.status === 'success' && <Success />}
      {requestInfo.status === 'error' && <Error />}
    </div>
  );
};

LearnerManagement.propTypes = {
  learnerAction: PropTypes.oneOf([
    'force-activation',
    'force-password',
  ]),
};

LearnerManagement.defaultProps = {
  learnerAction: 'force-activation',
};

export default LearnerManagement;
